import { Alert } from "@common/controls/Alert/component";
import Button from "@common/controls/Button/component";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormField } from "@common/controls/Form/component";
import { FormFieldContainer } from "@common/controls/Form/Field/component";
import FormFieldLabel from "@common/controls/Form/Field/Label";
import { IconAction } from "@common/controls/Icon/component";
import TextArea from "@common/controls/Inputs/TextArea/component";
import {
  type FormModalControlledProps,
  type FormModalControlledRenderParams
} from "@common/controls/Modal/Modals/Form/component";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { formatToHtml } from "@common/lib/format";
import { i18nFormat } from "@common/lib/i18n";
import { getApi } from "@common/modules/ai/api";
import classNames from "classnames";
import { isNull } from "lodash";
import React from "react";
import Modal from "../Modal";

type FormData = {
  text?: string;
  instructions: string;
};
type Props = Omit<FormModalControlledProps<FormData>, "children" | "defaultValues" | "submit"> & {
  text: string;
  onUse: (text: string) => void;
};

const AiModal = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const [results, setResults] = React.useState<string[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = React.useState<number | null>(null);

  const submit = React.useCallback(
    async (data: FormData) => {
      const previousResult = isNull(currentResultIndex) ? undefined : results[currentResultIndex];

      const res = await (data.text
        ? api.text.edit({
            text: data.text,
            instructions: data.instructions,
            previousResult
          })
        : api.text.generate({
            instructions: data.instructions,
            previousResult
          }));

      setResults([...results, res]);
      setCurrentResultIndex(results.length);
    },
    [results, currentResultIndex]
  );

  const onClose = React.useCallback(() => {
    setCurrentResultIndex(null);
    setResults([]);
    props.onClose && props.onClose();
  }, [props.onClose]);

  const defaultValues = React.useMemo(
    (): Partial<FormData> => ({
      text: props.text
    }),
    [props.text]
  );

  const selectedText = React.useMemo(() => formatToHtml(props.text), [props.text]);

  return (
    <Modal
      {...props}
      onClose={onClose}
      defaultValues={defaultValues}
      className={classNames("ai_modal _theme_ admin rich_editor_modal")}
      submit={submit}
      title={props.text ? i18n.editText : i18n.writeText}
      toast=""
    >
      {({ defaultValues, values }: FormModalControlledRenderParams<FormData>) => (
        <>
          {!!selectedText && (
            <FormFieldContainer>
              <FormFieldLabel label={i18n.selectedText} />
              <Alert color={SystemColor.Gray}>
                <div dangerouslySetInnerHTML={{ __html: selectedText }}></div>
              </Alert>
            </FormFieldContainer>
          )}

          <FormField
            isRequired
            label={i18n.instructions}
            name="instructions"
            Component={TextArea}
            componentProps={{
              maxLength: 512,
              placeholder: defaultValues.text
                ? i18n.richEditorAiEditInstructionsPlaceholder
                : i18n.richEditorAiGenerateInstructionsPlaceholder,
              rows: 4
            }}
            value={values.instructions}
          />

          {!isNull(currentResultIndex) && (
            <FormFieldContainer>
              <FormFieldLabel
                label={i18nFormat(i18n.richEditorAiModalResultLabel, [
                  "INDEX",
                  results.length > 1 ? `${currentResultIndex + 1}/${results.length}` : null
                ])}
              />
              <Alert color={SystemColor.Blue}>
                <div dangerouslySetInnerHTML={{ __html: formatToHtml(results[currentResultIndex]) }} />
              </Alert>
            </FormFieldContainer>
          )}

          <FormButtons className={results.length > 1 ? "with_arrows" : undefined}>
            {results.length > 1 && (
              <div className="arrows_container">
                <IconAction
                  size={36}
                  onClick={currentResultIndex === 0 ? undefined : () => setCurrentResultIndex(currentResultIndex! - 1)}
                  className={classNames("arrow_left", { disabled: currentResultIndex === 0 })}
                  icon={IconType.ArrowLeft}
                />
                <IconAction
                  size={36}
                  onClick={
                    currentResultIndex === results.length - 1
                      ? undefined
                      : () => setCurrentResultIndex(currentResultIndex! + 1)
                  }
                  className={classNames("arrow_right", { disabled: currentResultIndex === results.length - 1 })}
                  icon={IconType.ArrowRight}
                />
              </div>
            )}

            <div className="buttons_container">
              {isNull(currentResultIndex) ? (
                <SubmitButton>{i18n.write}</SubmitButton>
              ) : (
                <div className="fr fg sm">
                  <SubmitButton type="white">{i18n.rewrite}</SubmitButton>
                  <Button
                    type="primary"
                    onClick={() => {
                      props.onUse(results[currentResultIndex]);
                      setCurrentResultIndex(null);
                      setResults([]);
                    }}
                  >
                    {i18n.use}
                  </Button>
                </div>
              )}
            </div>
          </FormButtons>
        </>
      )}
    </Modal>
  );
};
export default AiModal;
